'use client';

import React from 'react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';

import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

import { Sparkles, Lightbulb, X } from 'lucide-react';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  descripcion: string;
  loading?: boolean;
  totalVariantes?: number;
  onAccept: () => void;
  onRegenerate?: () => void;
}

export const GenerateDescriptionDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  descripcion,
  loading = false,
  totalVariantes = 1,
  onAccept,
  onRegenerate,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        onInteractOutside={(e) => e.preventDefault()}
        className="
          z-[60] p-0 border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1c1c1e]
          [&>button:last-child]:hidden
          flex flex-col
          !bottom-0 !left-0 !top-auto !translate-x-0 !translate-y-0
          w-full max-w-none max-h-[90vh] rounded-t-2xl rounded-b-none
          data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom
          sm:!bottom-auto sm:!left-1/2 sm:!top-1/2 sm:!-translate-x-1/2 sm:!-translate-y-1/2
          sm:w-[95%] sm:max-w-lg sm:mx-auto sm:max-h-[90vh] sm:rounded-xl sm:shadow-xl
        "
      >
        {/* Header con X */}
        <div className="px-4 sm:px-6 pt-5 pb-3 shrink-0 relative">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl font-semibold text-[#103254] dark:text-blue-400 flex items-center gap-2 pr-10">
              <Sparkles className="w-5 h-5 shrink-0" />
              Generar descripción con IA
            </DialogTitle>
          </DialogHeader>
          <button
            onClick={() => onOpenChange(false)}
            className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 sm:px-6 pb-2 space-y-4">
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-3 text-blue-800 dark:text-blue-200 text-sm shadow-sm flex gap-3">
            <Lightbulb className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
            <div>
              Hemos generado una propuesta de descripción para tu empresa.
              {totalVariantes > 1 && (
                <span className="ml-1 font-medium">
                  Pulsa "Siguiente versión" para ver las {totalVariantes}{' '}
                  variantes disponibles.
                </span>
              )}
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 shadow-sm p-4 space-y-4 text-sm text-gray-700 dark:text-gray-300">
            <div>
              <Label className="text-gray-700 dark:text-gray-300">
                Descripción generada
              </Label>
              <Textarea
                value={descripcion}
                readOnly
                className="mt-1 resize-none border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 text-gray-900 dark:text-white min-h-[120px]"
              />
            </div>
          </div>
        </div>

        <DialogFooter className="px-4 sm:px-6 pt-2 pb-5 sm:pb-4 flex flex-col-reverse sm:flex-row gap-2 shrink-0 border-t border-gray-100 dark:border-white/10">
          {onRegenerate && (
            <Button
              onClick={onRegenerate}
              disabled={loading}
              variant="outline"
              className="w-full sm:w-auto h-12 sm:h-11 rounded-xl text-base sm:text-sm border-gray-300 dark:border-white/10 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-white/5"
            >
              {loading
                ? 'Generando…'
                : totalVariantes > 1
                ? 'Siguiente versión'
                : 'Generar otra'}
            </Button>
          )}

          <Button
            onClick={onAccept}
            disabled={!descripcion || loading}
            className="flex-1 h-12 sm:h-11 rounded-xl text-base sm:text-sm text-white font-semibold shadow-md bg-[#103254] hover:bg-[#0d2742] dark:bg-[#103254] dark:hover:bg-[#0d2742] disabled:opacity-50 transition-colors"
          >
            Usar descripción
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};